import React, { useState } from 'react';
import { ChevronDown, X, SlidersHorizontal } from 'lucide-react';
export interface FilterState {
  category: string;
  size: string;
  sort: string;
}
interface FilterBarProps {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
}
export function FilterBar({
  filters,
  setFilters
}: FilterBarProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const categories = ['All', 'Dresses', 'Tops', 'Skirts', 'Accessories'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL'];
  const sortOptions = ['Newest', 'Price: Low to High', 'Price: High to Low'];
  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setActiveDropdown(null);
  };
  const clearFilters = () => {
    setFilters({
      category: 'All',
      size: '',
      sort: 'Newest'
    });
  };
  const hasActiveFilters = filters.category !== 'All' || filters.size;
  const activeFilterCount = [filters.category !== 'All', filters.size].filter(Boolean).length;
  return <div className="w-full mb-6" data-id="element-414">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden flex justify-between items-center py-4 border-b border-[#E8E8E8]" data-id="element-415">
        <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)} className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]" data-id="element-416">
          <SlidersHorizontal size={16} data-id="element-417" />
          Filters
          {activeFilterCount > 0 && <span className="w-5 h-5 bg-[#D4A853] text-[#1A1A1A] text-xs rounded-full flex items-center justify-center" data-id="element-418">
              {activeFilterCount}
            </span>}
        </button>

        {/* Mobile Sort */}
        <div className="relative" data-id="element-419">
          <button onClick={() => toggleDropdown('sort')} className="flex items-center gap-1.5 text-sm text-[#1A1A1A]" data-id="element-420">
            <span data-id="element-421">{filters.sort}</span>
            <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'sort' ? 'rotate-180' : ''}`} data-id="element-422" />
          </button>
          {activeDropdown === 'sort' && <div className="absolute top-full right-0 mt-2 w-44 bg-white border border-[#E8E8E8] shadow-lg z-30 py-1" data-id="element-423">
              {sortOptions.map(option => <button key={option} onClick={() => handleFilterChange('sort', option)} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${filters.sort === option ? 'text-[#D4A853] bg-[#FAFAFA]' : 'text-[#1A1A1A] hover:bg-[#FAFAFA]'}`} data-id="element-424">
                  {option}
                </button>)}
            </div>}
        </div>
      </div>

      {/* Mobile Filters Panel */}
      {mobileFiltersOpen && <div className="lg:hidden py-6 border-b border-[#E8E8E8] space-y-6" data-id="element-425">
          {/* Category */}
          <div data-id="element-426">
            <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-3" data-id="element-427">
              Category
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-428">
              {categories.map(cat => <button key={cat} onClick={() => handleFilterChange('category', cat)} className={`px-4 py-2 text-sm border transition-all ${filters.category === cat ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'border-[#E8E8E8] text-[#1A1A1A] hover:border-[#1A1A1A]'}`} data-id="element-429">
                  {cat}
                </button>)}
            </div>
          </div>

          {/* Size */}
          <div data-id="element-430">
            <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-3" data-id="element-431">
              Size
            </p>
            <div className="flex gap-2" data-id="element-432">
              {sizes.map(size => <button key={size} onClick={() => handleFilterChange('size', filters.size === size ? '' : size)} className={`w-10 h-10 flex items-center justify-center text-sm border transition-all ${filters.size === size ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'border-[#E8E8E8] text-[#1A1A1A] hover:border-[#1A1A1A]'}`} data-id="element-433">
                  {size}
                </button>)}
            </div>
          </div>

          {hasActiveFilters && <button onClick={clearFilters} className="text-sm text-[#D4A853] hover:text-[#B8923F] flex items-center gap-1 transition-colors" data-id="element-434">
              <X size={14} data-id="element-435" /> Clear all filters
            </button>}
        </div>}

      {/* Desktop Filters */}
      <div className="hidden lg:flex items-center justify-between py-4 border-b border-[#E8E8E8]" data-id="element-436">
        <div className="flex items-center gap-8" data-id="element-437">
          {/* Category Dropdown */}
          <div className="relative" data-id="element-438">
            <button onClick={() => toggleDropdown('category')} className="flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#D4A853] transition-colors" data-id="element-439">
              <span className="text-[#6B6B6B]" data-id="element-440">Category:</span>
              <span className="font-medium" data-id="element-441">{filters.category}</span>
              <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'category' ? 'rotate-180' : ''}`} data-id="element-442" />
            </button>
            {activeDropdown === 'category' && <div className="absolute top-full left-0 mt-2 w-44 bg-white border border-[#E8E8E8] shadow-lg z-30 py-1" data-id="element-443">
                {categories.map(cat => <button key={cat} onClick={() => handleFilterChange('category', cat)} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${filters.category === cat ? 'text-[#D4A853] bg-[#FAFAFA]' : 'text-[#1A1A1A] hover:bg-[#FAFAFA]'}`} data-id="element-444">
                    {cat}
                  </button>)}
              </div>}
          </div>

          <div className="w-px h-4 bg-[#E8E8E8]" data-id="element-445"></div>

          {/* Size Filter */}
          <div className="flex items-center gap-3" data-id="element-446">
            <span className="text-sm text-[#6B6B6B]" data-id="element-447">Size:</span>
            <div className="flex gap-1.5" data-id="element-448">
              {sizes.map(size => <button key={size} onClick={() => handleFilterChange('size', filters.size === size ? '' : size)} className={`w-8 h-8 flex items-center justify-center text-xs font-medium transition-all ${filters.size === size ? 'bg-[#1A1A1A] text-white' : 'text-[#1A1A1A] hover:bg-[#F5F5F5]'}`} data-id="element-449">
                  {size}
                </button>)}
            </div>
          </div>

          {hasActiveFilters && <>
              <div className="w-px h-4 bg-[#E8E8E8]" data-id="element-450"></div>
              <button onClick={clearFilters} className="text-sm text-[#D4A853] hover:text-[#B8923F] flex items-center gap-1 transition-colors" data-id="element-451">
                <X size={14} data-id="element-452" /> Clear
              </button>
            </>}
        </div>

        {/* Sort Dropdown */}
        <div className="relative" data-id="element-453">
          <button onClick={() => toggleDropdown('sort')} className="flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#D4A853] transition-colors" data-id="element-454">
            <span className="text-[#6B6B6B]" data-id="element-455">Sort by:</span>
            <span className="font-medium" data-id="element-456">{filters.sort}</span>
            <ChevronDown size={14} className={`transition-transform ${activeDropdown === 'sort' ? 'rotate-180' : ''}`} data-id="element-457" />
          </button>
          {activeDropdown === 'sort' && <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-[#E8E8E8] shadow-lg z-30 py-1" data-id="element-458">
              {sortOptions.map(option => <button key={option} onClick={() => handleFilterChange('sort', option)} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${filters.sort === option ? 'text-[#D4A853] bg-[#FAFAFA]' : 'text-[#1A1A1A] hover:bg-[#FAFAFA]'}`} data-id="element-459">
                  {option}
                </button>)}
            </div>}
        </div>
      </div>
    </div>;
}